import { useState, useEffect, MouseEvent, useRef } from 'react';
import { useAudioPlayerContext } from './context/AudioPlayerContext';

export const ProgressBar = () => {
  const {
    audioRef,
    setTimeProgress,
    timeProgress,
    duration,
    lastProgressValue,
    setLastProgressValue,
  } = useAudioPlayerContext();

  const progressBarRef = useRef<HTMLInputElement>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  // Update progress bar value and style
  useEffect(() => {
    if (progressBarRef.current && !isDragging) {
      const progressPercentage = duration > 0 ? (timeProgress / duration) * 100 : 0;
      progressBarRef.current.value = timeProgress.toString();
      progressBarRef.current.style.setProperty('--range-progress', `${progressPercentage}%`);
      setLastProgressValue(timeProgress); // Store the last known progress value
    }
  }, [timeProgress, duration, isDragging, setLastProgressValue]);

  // Log or use lastProgressValue whenever the component re-renders
  useEffect(() => {
    // You can use lastProgressValue here for any other purpose
  }, [lastProgressValue]);

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current && progressBarRef.current) {
      const newTime = Math.max(0, Number(e.target.value));
      setTimeProgress(newTime);
      
      // Only update audio time when dragging stops
      if (!isDragging) {
        audioRef.current.currentTime = newTime;
      }
    }
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    if (isDragging && audioRef.current && progressBarRef.current) {
      const newTime = Number(progressBarRef.current.value);
      audioRef.current.currentTime = newTime;
      setIsDragging(false);
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLInputElement>) => {
    if (progressBarRef.current && duration) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const newHoverTime = (offsetX / rect.width) * duration;

      setHoverTime(newHoverTime);
      setHoverPosition(offsetX);
    }
  };

  const handleMouseLeave = () => {
    setHoverTime(null);
    if (isDragging) {
      handleMouseUp();
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div className="w-full h-1">
      <input
        className="absolute top-[-11px] left-0 w-full h-full appearance-none cursor-pointer 
          [&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded-full
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 
          [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white 
          [&::-webkit-slider-thumb]:mt-[-2px] [&::-webkit-slider-thumb]:hover:scale-125
          [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-200
          [&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded-full
          [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full 
          [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:border-2 
          [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:hover:scale-125
          [&::-moz-range-thumb]:transition-transform [&::-moz-range-thumb]:dmturation-200"
        type="range"
        ref={progressBarRef}
        min="0"
        max={duration || 100}
        step="0.01"
        value={timeProgress}
        onChange={handleProgressChange}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          background: `linear-gradient(to right, #C81C22 0%, #C81C22 ${(timeProgress / (duration || 1)) * 100}%, #6C6C6C ${(timeProgress / (duration || 1)) * 100}%, #6C6C6C 100%)`,
        }}
      />
      {hoverTime !== null && (
        <div
          className="absolute text-sm bg-secondary-500 text-white px-1 py-0.5 rounded opacity-100 transition-opacity duration-200 pointer-events-none font-kanit"
          style={{
            top: '-25px',
            left: `${hoverPosition}px`,
            transform: 'translateX(-50%)',
          }}
        >
          {formatTime(hoverTime)}
        </div>
      )}
    </div>
  );
};